import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useThemeMode } from '@/providers/themeMode';
import type { MobileTheme } from './constants';
import { MobilePreviewContext } from './mobilePreviewContext';

interface MobilePreviewProviderProps {
  /** Overrides the default, which follows the admin's own theme. */
  initialTheme?: MobileTheme;
  /** The whole preview — controls above the frame included. */
  children: ReactNode;
}

/**
 * Owns the previewed phone's theme. Wrap the controls *and* the frame:
 * the toggle sits outside the device chrome but drives what is inside
 * it, so both have to read the same state.
 *
 * The phone opens matching the admin's appearance — a dark admin shows a
 * dark phone — and the toggle takes it from there; a later admin theme
 * switch leaves an already-open preview alone.
 */
export const MobilePreviewProvider = ({
  initialTheme,
  children,
}: MobilePreviewProviderProps) => {
  const { isDark } = useThemeMode();
  const [mobileTheme, setMobileTheme] = useState<MobileTheme>(
    initialTheme ?? (isDark ? 'dark' : 'light')
  );
  const value = useMemo(() => ({ mobileTheme, setMobileTheme }), [mobileTheme]);

  return (
    <MobilePreviewContext.Provider value={value}>
      {children}
    </MobilePreviewContext.Provider>
  );
};
